import { supabase } from '../lib/supabase';

export const assetService = {
  /**
   * Helper to get effective user ID (considering impersonation)
   */
  _getEffectiveUserId() {
    try {
      const impersonationData = localStorage.getItem('admin_impersonation');
      if (impersonationData) {
        const parsedData = JSON.parse(impersonationData);
        return parsedData?.impersonatedUserId || null;
      }
      return null;
    } catch (error) {
      console.error('Error checking impersonation:', error);
      return null;
    }
  },

  /**
   * Get all assets for the current user
   */
  async getAll() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if admin is impersonating a user
      const impersonatedUserId = this._getEffectiveUserId();
      const queryUserId = impersonatedUserId || user?.id;

      const { data, error } = await supabase
        ?.from('assets')
        ?.select('*')
        ?.eq('user_id', queryUserId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert snake_case to camelCase for React
      return data?.map(asset => ({
        id: asset?.id,
        userId: asset?.user_id,
        assetName: asset?.asset_name,
        assetType: asset?.asset_type,
        assetValue: asset?.asset_value,
        currency: asset?.currency,
        location: asset?.location,
        assignedStaffMember: asset?.assigned_staff_member,
        serialNumber: asset?.serial_number,
        warrantyExpiry: asset?.warranty_expiry,
        specifications: asset?.specifications,
        isInsured: asset?.is_insured,
        purchaseDate: asset?.purchase_date,
        notes: asset?.notes,
        createdAt: asset?.created_at,
        updatedAt: asset?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching assets:', error);
      throw error;
    }
  },

  /**
   * Get statistics for assets
   */
  async getStatistics() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if admin is impersonating a user
      const impersonatedUserId = this._getEffectiveUserId();
      const queryUserId = impersonatedUserId || user?.id;

      const { data, error } = await supabase
        ?.from('assets')
        ?.select('asset_value, currency, is_insured, location')
        ?.eq('user_id', queryUserId);

      if (error) throw error;

      // Calculate statistics using currency from assets
      const totalAssets = data?.length || 0;
      const totalValue = data?.reduce((sum, asset) => sum + (asset?.asset_value || 0), 0);
      const insuredCount = data?.filter(asset => asset?.is_insured)?.length || 0;
      const inOfficeCount = data?.filter(asset => asset?.location === 'in_office')?.length || 0;
      const remoteCount = data?.filter(asset => asset?.location === 'remote')?.length || 0;

      return {
        totalAssets,
        totalValue,
        insuredCount,
        inOfficeCount,
        remoteCount
      };
    } catch (error) {
      console.error('Error fetching asset statistics:', error);
      throw error;
    }
  },

  /**
   * Get asset by ID
   */
  async getById(assetId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if admin is impersonating a user
      const impersonatedUserId = this._getEffectiveUserId();
      const queryUserId = impersonatedUserId || user?.id;

      const { data, error } = await supabase
        ?.from('assets')
        ?.select('*')
        ?.eq('id', assetId)
        ?.eq('user_id', queryUserId)
        ?.single();

      if (error) throw error;

      // Convert snake_case to camelCase
      return {
        id: data?.id,
        userId: data?.user_id,
        assetName: data?.asset_name,
        assetType: data?.asset_type,
        assetValue: data?.asset_value,
        currency: data?.currency,
        location: data?.location,
        assignedStaffMember: data?.assigned_staff_member,
        serialNumber: data?.serial_number,
        warrantyExpiry: data?.warranty_expiry,
        specifications: data?.specifications,
        isInsured: data?.is_insured,
        purchaseDate: data?.purchase_date,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error fetching asset:', error);
      throw error;
    }
  },

  /**
   * Create new asset
   */
  async create(assetData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // For "other" asset type, store the custom type in specifications
      const specifications = { ...assetData?.specifications };
      if (assetData?.assetType === 'other' && assetData?.otherAssetType) {
        specifications.customAssetType = assetData?.otherAssetType;
      }

      // Convert camelCase to snake_case
      const insertData = {
        user_id: user?.id,
        asset_name: assetData?.assetName,
        asset_type: assetData?.assetType,
        asset_value: assetData?.assetValue,
        currency: assetData?.currency, // Store currency with asset
        location: assetData?.location,
        assigned_staff_member: assetData?.assignedStaffMember,
        serial_number: assetData?.serialNumber || null,
        warranty_expiry: assetData?.warrantyExpiry || null,
        specifications: specifications,
        is_insured: assetData?.isInsured || false,
        purchase_date: assetData?.purchaseDate || null,
        notes: assetData?.notes || null
      };

      const { data, error } = await supabase
        ?.from('assets')
        ?.insert([insertData])
        ?.select()
        ?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        userId: data?.user_id,
        assetName: data?.asset_name,
        assetType: data?.asset_type,
        assetValue: data?.asset_value,
        currency: data?.currency,
        location: data?.location,
        assignedStaffMember: data?.assigned_staff_member,
        serialNumber: data?.serial_number,
        warrantyExpiry: data?.warranty_expiry,
        specifications: data?.specifications,
        isInsured: data?.is_insured,
        purchaseDate: data?.purchase_date,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error creating asset:', error);
      throw error;
    }
  },

  /**
   * Update existing asset
   */
  async update(assetId, assetData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case
      const updateData = {
        asset_name: assetData?.assetName,
        asset_type: assetData?.assetType,
        asset_value: assetData?.assetValue,
        currency: assetData?.currency, // Update currency if changed
        location: assetData?.location,
        assigned_staff_member: assetData?.assignedStaffMember,
        serial_number: assetData?.serialNumber || null,
        warranty_expiry: assetData?.warrantyExpiry || null,
        specifications: assetData?.specifications || {},
        is_insured: assetData?.isInsured || false,
        purchase_date: assetData?.purchaseDate || null,
        notes: assetData?.notes || null
      };

      const { data, error } = await supabase
        ?.from('assets')
        ?.update(updateData)
        ?.eq('id', assetId)
        ?.eq('user_id', user?.id)
        ?.select()
        ?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        userId: data?.user_id,
        assetName: data?.asset_name,
        assetType: data?.asset_type,
        assetValue: data?.asset_value,
        currency: data?.currency,
        location: data?.location,
        assignedStaffMember: data?.assigned_staff_member,
        serialNumber: data?.serial_number,
        warrantyExpiry: data?.warranty_expiry,
        specifications: data?.specifications,
        isInsured: data?.is_insured,
        purchaseDate: data?.purchase_date,
        notes: data?.notes,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Error updating asset:', error);
      throw error;
    }
  },

  // Delete asset
  async delete(assetId) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase?.from('assets')?.delete()?.eq('id', assetId)?.eq('user_id', user?.id);

    if (error) throw error;
  }
};